import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Button, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
//api
import { getProvinces } from 'api/company';
import { getLimitedDeliveries } from 'api/transport';
//slices
import { setShippingFee, setIsShippingAvailable } from 'slices/cart';
import { companySelector } from 'slices/company';
//components
import { InputCustom } from 'components/input';

const AddressForm = ({
  data,
  onChange,
  onCancel,
  onSave,
  disableBtnSave,
  countries,
  additionalInfoHeader = null,
  isShippingAddressDetails = false,
  isBillingAddressDetails = false,
}) => {
  const { t } = useTranslation(['backoffice', 'shipping']);
  const dispatch = useDispatch();
  const { shipping_cost } = useSelector(companySelector);
  const [provinces, setProvinces] = useState([]);
  const [limitedDeliveries, setLimitedDeliveries] = useState([]);
  const [isLimitedDeliveryMatch, setIsLimitedDeliveryMatch] = useState(false);
  const [addressError, setAddressError] = useState('');

  const validateSpanishAddress = (address) => {
    if (!address || address.trim() === '') {
      return t('camporequerido');
    }
    const spanishAddressPattern =
      /^(Calle|Avenida|Plaza|Paseo|Carretera|Camino|Vía|Ronda|Travesía|Ctra)\s+[A-Za-zÀ-ÿ\s,.0-9º°-]+$/i;
    if (!spanishAddressPattern.test(address)) {
      return t('enterValidAddress');
    }
    return '';
  };

  const handleLimitedDeliveries = async () => {
    try {
      const { data } = await getLimitedDeliveries();
      if (data) {
        setLimitedDeliveries(data.results);
      }
    } catch (error) {
      console.error('Error getProvinces-country', error);
    }
  };

  useEffect(() => {
    isShippingAddressDetails && handleLimitedDeliveries();
  }, []);

  const handleGetProvincesCountry = async (country = '') => {
    try {
      const { data } = await getProvinces({ country_code: `${country}` });
      if (data) {
        const formatedProvinces = data.map((prov) => ({
          value: prov.id,
          label: prov.nombre,
        }));
        setProvinces(formatedProvinces);
      }
    } catch (error) {
      console.error('Error getProvinces-country', error);
    }
  };

  useEffect(() => {
    data?.country?.value && handleGetProvincesCountry(data?.country?.value);
  }, [data?.country]);

  const checkDeliveryProvince = (provinceSelected) => {
    dispatch(setIsShippingAvailable({ availability: true, message: '' }));
    onChange({ ...data, province: provinceSelected, postal_code: '' });
    if (isShippingAddressDetails) {
      const limitedDeliveryMatch = limitedDeliveries.find(
        (limitDelivery) => limitDelivery.province === provinceSelected.value,
      );
      setIsLimitedDeliveryMatch(limitedDeliveryMatch);
      if (limitedDeliveryMatch) {
        if (limitedDeliveryMatch?.not_delivered) {
          //! NO SE REALIZA ENVIO
          dispatch(setIsShippingAvailable({ availability: false, message: 'notDeliverProvince' }));
        }
      } else {
        //! ENVIO NORMAL
        setIsLimitedDeliveryMatch(false);
        dispatch(setIsShippingAvailable({ availability: true, message: '' }));
      }
    } else return;
  };

  const checkDeliveryPostalCode = (postalCode) => {
    dispatch(setIsShippingAvailable({ availability: true, message: '' }));
    if (isShippingAddressDetails) {
      const postalCodeLimitedMatch =
        isLimitedDeliveryMatch && isLimitedDeliveryMatch?.postal_codes_list.includes(postalCode);
      if (postalCodeLimitedMatch) {
        //! NO SE REALIZA ENVIO
        dispatch(setIsShippingAvailable({ availability: false, message: 'notDeliverPostalCode' }));
      } else {
        if (isLimitedDeliveryMatch?.is_special_price) {
          //! ENVIO CON PRECIO ESPECIAL
          dispatch(setShippingFee(isLimitedDeliveryMatch?.special_price));
        } else {
          //? ENVIO NORMAL
          setIsLimitedDeliveryMatch(false);
          dispatch(setShippingFee(shipping_cost)); //! PRECIO ENVIO DEFAULT
        }
      }
    } else return;
  };

  const renderButtonSaveText = () => {
    if (disableBtnSave) {
      return <Spinner animation="grow" variant="dark" />;
    } else if (isShippingAddressDetails || isBillingAddressDetails) {
      return <span>{t('saveAddressDefault').toUpperCase()}</span>;
    } else {
      return <span>{t('save').toUpperCase()}</span>;
    }
  };

  return (
    <Row>
      {additionalInfoHeader && <h4 className="mx-2 my-4">{additionalInfoHeader}</h4>}
      <Col xs={12} md={4}>
        <p className="mt-4">{t('name')}</p>
        <InputCustom
          type="text"
          name="firstname"
          placeholder={t('name')}
          title={t('name')}
          value={data?.first_name}
          onChange={(e) => onChange({ ...data, first_name: e.target.value })}
        />
      </Col>
      <Col xs={12} md={4}>
        <p className="mt-4">{t('apellido')}</p>
        <InputCustom
          type="text"
          name="lastname"
          placeholder={t('apellido')}
          title={t('apellido')}
          value={data?.last_name}
          onChange={(e) => onChange({ ...data, last_name: e.target.value })}
        />
      </Col>
      <Col xs={12} md={4}>
        <p className="mt-4">{t('correoelectronico')}</p>
        <InputCustom
          type="text"
          placeholder={t('correoelectronico')}
          title={t('correoelectronico')}
          value={data?.email}
          onChange={(e) => onChange({ ...data, email: e.target.value })}
        />
      </Col>
      <Col xs={12} md={4}>
        <p className="mt-4">{t('telefono')}</p>
        <InputCustom
          type="text"
          placeholder={t('telefono')}
          title={t('telefono')}
          value={data?.phone}
          onChange={(e) => onChange({ ...data, phone: e.target.value })}
        />
      </Col>
      <Col xs={12} md={4}>
        <p className="mt-4">{t('direccion')}</p>
        <InputCustom
          type="text"
          placeholder={t('direccion')}
          title={t('direccion')}
          value={data?.address}
          onChange={(e) => {
            const newAddress = e.target.value;
            const error = validateSpanishAddress(newAddress);
            setAddressError(error);
            onChange({ ...data, address: newAddress });
          }}
          isInvalid={!!addressError}
          required
        />
        {addressError && <div className="text-danger small mt-1">{addressError}</div>}
      </Col>
      <Col xs={12} md={4}>
        <p className="mt-4">{t('pais')}</p>
        <Select
          placeholder={t('pais')}
          title={t('pais')}
          className="mb-3"
          options={countries}
          value={data?.country}
          onChange={(selectedCountry) => onChange({ ...data, country: selectedCountry })}
        />
      </Col>
      <Col xs={12} md={4}>
        <p className="mt-4">{t('province')}</p>
        <Select
          placeholder={t('province')}
          title={t('province')}
          className="mb-3"
          options={provinces}
          value={data?.province}
          onChange={(selectedProvince) => checkDeliveryProvince(selectedProvince)}
        />
      </Col>
      <Col xs={12} md={4}>
        <p className="mt-4">{t('localidad')}</p>
        <InputCustom
          type="text"
          placeholder={t('localidad')}
          title={t('localidad')}
          value={data?.locality}
          onChange={(e) => onChange({ ...data, locality: e.target.value })}
        />
      </Col>
      <Col xs={12} md={4}>
        <p className="mt-4">{t('postalcode')}</p>
        <InputCustom
          type="text"
          name="postalcode"
          placeholder={t('postalcode')}
          title={t('postalcode')}
          value={data?.postal_code}
          onChange={(e) => {
            onChange({ ...data, postal_code: e.target.value });
            checkDeliveryPostalCode(e.target.value);
          }}
        />
      </Col>
      <Col xs={12} md={4}>
        <Button variant="outline-secondary mt-4" onClick={onCancel}>
          {t('cancel').toUpperCase()}
        </Button>{' '}
        <Button className="blue-btn mt-4" disabled={disableBtnSave || !!addressError} onClick={onSave}>
          {renderButtonSaveText()}
        </Button>
      </Col>
    </Row>
  );
};

AddressForm.propTypes = {
  data: PropTypes.object,
  onChange: PropTypes.func,
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
  disableBtnSave: PropTypes.bool,
  countries: PropTypes.array,
  additionalInfoHeader: PropTypes.string,
  isShippingAddressDetails: PropTypes.bool,
  isBillingAddressDetails: PropTypes.bool,
};

export default AddressForm;
